<script lang="ts" setup>
// ==================================================================
//                         Imports
// ==================================================================
import { useI18n } from "vue-i18n"
import * as yup from "yup"
import { useField, useForm } from "vee-validate"

type Props = {
    usage: string
    installationType: string
}
defineProps<Props>()
defineEmits(["update:usage", "update:installationType"])
// ==================================================================
//                         Lang
// ==================================================================
const { t } = useI18n({
    messages: {
        fr: {
            info_message:
                "Renseignez les informations d’utilisation de l’installation",
            label: {
                usage: "Usage du bâtiment",
                installation: "Type d'installation",
            },
        },
    },
})
// =================================================================
//                        Validation
// =================================================================
const schema = yup.object({
    usage: yup.string().required("L'usage du bâtiment est requis"),
    installationType: yup
        .string()
        .required("Le type d'installation est requis"),
})
// Initialisation du formulaire
const { errors, validateField } = useForm({
    validationSchema: schema,
})

const { value: modelUsage } = useField<string>("usage", undefined, {
    syncVModel: "usage",
})
const { value: modelInstallationType } = useField<string>(
    "installationType",
    undefined,
    {
        syncVModel: "installationType",
    }
)
// ==================================================================
//                         Constants
// ==================================================================
const BUILDING_USAGE = [
    {
        label: "Résidentiel",
        items: [
            {
                value: "maison_individuelle",
                name: "Maison individuelle",
            },
            {
                value: "immeuble_habitation_prive",
                name: "Immeuble d'habitation privé",
            },
            {
                value: "logements_sociaux",
                name: "Logements sociaux",
            },
            {
                value: "residences",
                name: "Résidences étudiantes, seniors et foyers",
            },
        ],
    },
    {
        label: "Commercial",
        items: [
            {
                value: "magasins",
                name: "Magasins, agences et points de vente",
            },
            {
                value: "centres_commerciaux",
                name: "Centres commerciaux",
            },
            {
                value: "restaurants_bars",
                name: "Restaurants, bars et cafés",
            },
            {
                value: "supermarches",
                name: "Supermarchés",
            },
        ],
    },
    {
        label: "Bureaux",
        items: [
            {
                value: "immeubles_bureaux",
                name: "Immeubles de bureaux",
            },
            {
                value: "centres_affaires",
                name: "Centres d'affaires et espaces de coworking",
            },
        ],
    },
    {
        label: "Industriel",
        items: [
            {
                value: "usines",
                name: "Usines et locaux industriels",
            },
            {
                value: "entrepots",
                name: "Entrepôts / Centres de distribution",
            },
        ],
    },
    {
        label: "Éducatif",
        items: [
            {
                value: "creches",
                name: "Crèches",
            },
            {
                value: "ecoles",
                name: "Écoles et établissements du 2nd degré",
            },
            {
                value: "universites",
                name: "Centres de formation et Universités",
            },
        ],
    },
    {
        label: "Tourisme",
        items: [
            {
                value: "hotels_auberges_campings",
                name: "Hôtels / Auberges / Campings",
            },
            {
                value: "residences_vacances",
                name: "Résidences de vacances",
            },
        ],
    },
    {
        label: "Bâtiments publics",
        items: [
            {
                value: "etablissements_administratifs",
                name: "Etablissements administratifs",
            },
            {
                value: "etablissements_publics",
                name: "Etablissements publics recevant du public",
            },
        ],
    },
    {
        label: "Culturel et récréatif",
        items: [
            {
                value: "theatres_salles_concerts_congres",
                name: "Théâtres et salles de concert et de congrès",
            },
            {
                value: "cinemas",
                name: "Cinéma",
            },
            {
                value: "musees",
                name: "Conservatoires, musées, bibliothèque et médiathèques",
            },
            {
                value: "equipements_sportifs",
                name: "Equipements sportifs",
            },
        ],
    },
    {
        label: "Religieux",
        items: [
            {
                value: "lieux_religieux",
                name: "Lieux de culte et de pratique religieuse",
            },
        ],
    },
    {
        label: "Santé",
        items: [
            {
                value: "laboratoires",
                name: "Laboratoires d’analyse ou de recherche",
            },
            {
                value: "hopitaux",
                name: "Cliniques et Hôpitaux",
            },
            {
                value: "ehpads_maisons_retraites",
                name: "EHPAD et maisons de retraites",
            },
        ],
    },
    {
        label: "Exploitations agricoles et forestières",
        items: [
            {
                value: "exploitations_agricoles",
                name: "Exploitations agricoles et forestières",
            },
            {
                value: "exploitations_renouvelables",
                name: "Exploitations IRVE, éoliennes ou solaires",
            },
        ],
    },
]
const INSTALLATION_TYPE = [
    {
        value: "parking_privés_bâtiments",
        name: "Parking privé",
    },
    {
        value: "parking_ouvert_public",
        name: "Parking ouvert au public ",
    },
]

defineExpose({
    validate: async () => {
        const { valid: validUsage } = await validateField("usage")
        const { valid: validInstallationType } =
            await validateField("installationType")

        return validUsage && validInstallationType
    },
})
</script>
<template>
    <div class="panel-building flex flex-col gap-6">
        <BaseMessage class="panel-building__errors" severity="info">
            {{ t("info_message") }}</BaseMessage
        >
        <form novalidate class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BaseInputWrapper
                :label="t('label.usage')"
                input-id="usage"
                :error="errors.usage"
            >
                <BaseSelect
                    input-id="usage"
                    v-model="modelUsage"
                    :options="BUILDING_USAGE"
                    option-label="name"
                    option-value="value"
                    option-group-label="label"
                    option-group-children="items"
                    :invalid="!!errors.usage"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                input-id="installationType"
                :label="t('label.installation')"
                :error="errors.installationType"
            >
                <BaseSelect
                    input-id="installationType"
                    v-model="modelInstallationType"
                    :options="INSTALLATION_TYPE"
                    option-label="name"
                    option-value="value"
                    :invalid="!!errors.installationType"
                />
            </BaseInputWrapper>
        </form>
        <slot name="error"></slot>
    </div>
</template>
